import React, { useEffect, useState } from "react";
import "../fish-map/FilterMenu.css";
import Select from "react-select";
import { Vessel } from "../../fish-map/layers/ais";
import { GeoPosition, Species } from "../../types";
import pallas, { FacilityProperties } from "../../services/pallas";
import { Route } from "../../fish-map/layers/route";
import { Loader } from "../../common/Loader";
import { useTranslation } from "../../i18n";

type RouteMenuProps = {
  aisVessels: { [key: string]: Vessel };
  routeInput: RouteInput;
  setRouteInput: React.Dispatch<React.SetStateAction<RouteInput>>;
  setRouteData: React.Dispatch<React.SetStateAction<Route | undefined>>;
};

export type RouteInput = {
  voyage: {
    start?: GeoPosition;
    stop?: GeoPosition;
    n_days: number;
    preferredSpecies: string[];
  };
  vesselInfo: {
    remainingFuel: number;
    remainingCargoCapacity: number;
  };
};

export const defaultRouteInput = {
  voyage: {
    n_days: 5,
    preferredSpecies: [Species(s => s)[0].value],
  },
  vesselInfo: {
    remainingFuel: 30000,
    remainingCargoCapacity: 20,
  },
};

export function useRouteInput() {
  return useState<RouteInput>(defaultRouteInput);
}

function RouteMenu(props: RouteMenuProps) {
  const t = useTranslation();
  const routeInput = props.routeInput;
  const setRouteInput = props.setRouteInput;
  const [facilities, setFacilities] = useState<FacilityProperties[]>([]);

  const [selectedVessel, setSelectedVessel] = useState<Vessel | undefined>();
  const [useLatLngInput, setUseLatLngInput] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState<
    number | undefined
  >();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    pallas.getFacilites().then((facilityDoc) => {
      setFacilities(facilityDoc.features.map((f) => f.properties));
    });
  }, []);

  const selectVessel = (vessel?: Vessel) => {
    if (!vessel) return;
    setSelectedVessel(vessel);
    const point = {
      lat: vessel.geometry.coordinates[1],
      lng: vessel.geometry.coordinates[0],
    };
    setRouteInput({
      ...routeInput,
      voyage: { ...routeInput.voyage, start: point },
    });
  };

  const selectFacility = (facilityName: string) => {
    const facilityIndex = facilities.findIndex((v) => v.name === facilityName);
    setSelectedFacility(facilityIndex);
    const facility = facilities[facilityIndex];
    setRouteInput({
      ...routeInput,
      voyage: {
        ...routeInput.voyage,
        stop: { lat: facility.lat, lng: facility.lng },
      },
    });
  };

  const fetchRoute = async () => {
    if (
      routeInput.voyage.start?.lat &&
      routeInput.voyage.start?.lng &&
      routeInput.voyage.stop?.lat &&
      routeInput.voyage.stop?.lng &&
      routeInput.voyage.preferredSpecies.length &&
      routeInput.voyage.n_days > 0 &&
      routeInput.vesselInfo.remainingCargoCapacity > 0 &&
      routeInput.vesselInfo.remainingFuel > 0
    ) {
      setLoading(true);
      try {
        const routeInputWithKilos = { ...routeInput, vesselInfo: { ...routeInput.vesselInfo, remainingCargoCapacity: routeInput.vesselInfo.remainingCargoCapacity * 1000 } };
        const res = await pallas.getRoute(routeInputWithKilos);
        props.setRouteData(res);
      } finally {
        setLoading(false);
      }
    } else {
      alert(t("RouteMenu.FillAllFields"));
    }
  };

  const vesselOptionsList = Object.values(props.aisVessels).map((v) => ({
    value: v,
    label: `${v.properties.name} - ${v.properties.mmsi}`,
  }));
  const facilitiesOptionsList = facilities.map((f) => ({
    value: f.name,
    label: f.name,
  }));
  return (
    <div className="selectorWrapper">
      <h2 className="selectorTitle">{t("RouteMenu.Title")}</h2>
      <label htmlFor="species">{t("RouteMenu.SpeciesSelectorLabel")}</label>
      <Select
        onChange={(v) =>
          setRouteInput({
            ...routeInput,
            voyage: {
              ...routeInput.voyage,
              preferredSpecies: v.map((s) => s.value),
            },
          })
        }
        value={routeInput.voyage.preferredSpecies.map((s) => ({
          value: s,
          label: t(`Species.${s}`),
        }))}
        options={Species(t)}
        isMulti={true}
        placeholder={t("RouteMenu.SpeciesSelectorPlaceholder")}
        name="species"
      />
      <div>
        <br />
      </div>
      {useLatLngInput ? (
        <>
          <label htmlFor="lat">
            {t("RouteMenu.PositionSelectorLabel")}{" "}
            <a href="#" onClick={() => setUseLatLngInput(!useLatLngInput)}>
              {t("RouteMenu.VesselSelectionToggleOn")}
            </a>
          </label>
          <input
            type="number"
            pattern="[0-9]*"
            name="lat"
            id="lat"
            value={routeInput.voyage.start?.lat}
            placeholder={t("RouteMenu.Position.Latitude")}
            onChange={(v) =>
              setRouteInput({
                ...routeInput,
                voyage: {
                  ...routeInput.voyage,
                  start: {
                    ...routeInput.voyage.start,
                    lat: parseFloat(v.target.value),
                  } as any,
                },
              })
            }
          ></input>
          <input
            type="number"
            pattern="[0-9]*"
            name="lng"
            id="lng"
            value={routeInput.voyage.start?.lng}
            placeholder={t("RouteMenu.Position.Longitude")}
            onChange={(v) =>
              setRouteInput({
                ...routeInput,
                voyage: {
                  ...routeInput.voyage,
                  start: {
                    ...routeInput.voyage.start,
                    lng: parseFloat(v.target.value),
                  } as any,
                },
              })
            }
          ></input>
        </>
      ) : (
        <>
          <label className="filterLabel" htmlFor="vessel">
            {t("RouteMenu.VesselSelectorLabel")}{" "}
            <a href="#" onClick={() => setUseLatLngInput(!useLatLngInput)}>
              {t("RouteMenu.PositionSelectionToggleOn")}
            </a>
          </label>
          <Select
            value={
              selectedVessel !== undefined
                ? {
                    label: `${selectedVessel.properties.name} - ${selectedVessel.properties.mmsi}`,
                    value: selectedVessel,
                  }
                : undefined
            }
            onChange={(v) => selectVessel(v?.value)}
            options={vesselOptionsList}
            isMulti={false}
            placeholder={t("RouteMenu.VesselSelectorPlaceholder")}
            name="vessel"
          />
        </>
      )}
      <label className="filterLabel" htmlFor="fishery">
        {t("RouteMenu.FishFacilityLabel")}
      </label>
      <Select
        value={
          selectedFacility !== undefined
            ? facilitiesOptionsList[selectedFacility]
            : undefined
        }
        onChange={(c) => c?.value && selectFacility(c.value)}
        options={facilitiesOptionsList}
        isMulti={false}
        placeholder={t("RouteMenu.FishFacilityPlaceholder")}
        name="fishery"
      />
      <label className="filterLabel" htmlFor="n_days">
        {t("RouteMenu.DaysOut")}
      </label>
      <input
        type="number"
        pattern="[0-9]*"
        name="n_days"
        id="n_days"
        className="inputField"
        value={routeInput.voyage.n_days}
        onChange={(v) =>
          setRouteInput({
            ...routeInput,
            voyage: { ...routeInput.voyage, n_days: parseInt(v.target.value) },
          })
        }
      ></input>

      <h3 className="bigLabel">{t("RouteMenu.Vessel.InfoTitle")}</h3>
      <label className="filterLabel" htmlFor="remainingFuel">
        {t("RouteMenu.Vessel.FuelLabel")}
      </label>
      <input
        type="number"
        pattern="[0-9]*"
        name="remainingFuel"
        id="remainingFuel"
        className="inputField"
        value={routeInput.vesselInfo.remainingFuel}
        onChange={(v) =>
          setRouteInput({
            ...routeInput,
            vesselInfo: {
              ...routeInput.vesselInfo,
              remainingFuel: parseInt(v.target.value),
            },
          })
        }
      ></input>
      <label className="filterLabel" htmlFor="remainingCargoCapacity">
        {t("RouteMenu.Vessel.CargoLabel")}
      </label>
      <input
        type="number"
        pattern="[0-9]*"
        name="remainingCargoCapacity"
        id="remainingCargoCapacity"
        className="inputField"
        value={routeInput.vesselInfo.remainingCargoCapacity}
        onChange={(v) =>
          setRouteInput({
            ...routeInput,
            vesselInfo: {
              ...routeInput.vesselInfo,
              remainingCargoCapacity: parseInt(v.target.value),
            },
          })
        }
      ></input>
      <div>
        <br />
      </div>
      {loading ? (
        <>
          <p>{t("RouteMenu.Loading")}</p>
          <Loader />
        </>
      ) : (
        <button className="filterButton" onClick={fetchRoute}>
          {t("RouteMenu.SubmitButton")}
        </button>
      )}
    </div>
  );
}

export default RouteMenu;
